import moment from 'moment'
import {
  ACCOUNTING_REPORT_ROWS,
  getLegacyReportDefinition,
  getReportByCode,
  LEGACY_VALUE_LABELS,
  LEGACY_VALUE_ORDER,
  REPORT_CODES
} from './reportMetadata'
import { formatDate } from './reportFormatters'

const VALUE_LABELS = {
  current: 'Текущий период',
  previous: 'Предыдущий период',
  preprevious: 'Позапрошлый период',
  opening: 'Остаток на начало',
  increase: 'Увеличение',
  decrease: 'Уменьшение',
  closing: 'Остаток на конец',
  income: 'Приток',
  outcome: 'Отток',
  net: 'Чистое движение'
}

const NON_VALUE_FIELDS = [
  'id',
  'key',
  'code',
  'account',
  'name',
  'section',
  'children',
  'isTotal'
]

const CANONICAL_ROW_DEFINITIONS = {
  [REPORT_CODES.MONEY_MOVE]: {
    current: { name: 'Текущие операции', section: 'Денежные потоки', isTotal: true },
    investing: { name: 'Инвестиционные операции', section: 'Денежные потоки', isTotal: true },
    financing: { name: 'Финансовые операции', section: 'Денежные потоки', isTotal: true },
    unknown: { name: 'Неклассифицированные операции', section: 'Денежные потоки' }
  }
}

function definitionMap(reportCode) {
  const canonical = CANONICAL_ROW_DEFINITIONS[reportCode] || {}
  return (ACCOUNTING_REPORT_ROWS[reportCode] || []).reduce((acc, item) => {
    acc[item.key] = item
    acc[item.code] = item
    return acc
  }, { ...canonical })
}

function rowValues(row) {
  if (row.values && typeof row.values === 'object' && !Array.isArray(row.values)) {
    return row.values
  }
  return Object.keys(row || {}).reduce((acc, key) => {
    if (!NON_VALUE_FIELDS.includes(key)) {
      acc[key] = row[key]
    }
    return acc
  }, {})
}

function normalizeRows(reportCode, rows) {
  const definitions = definitionMap(reportCode)
  return (rows || []).map((row, index) => {
    const key = row.key || row.code || row.account || `row-${index}`
    const definition = definitions[key] || {}
    return {
      id: `${reportCode}-${key}-${index}`,
      key,
      code: definition.code || row.code || row.account || '',
      name: row.name || definition.name || row.account || row.code || 'Строка отчета',
      section: row.section || definition.section || '',
      values: rowValues(row),
      isTotal: Boolean(row.isTotal || definition.isTotal),
      children: Array.isArray(row.children) ? normalizeRows(reportCode, row.children) : []
    }
  })
}

function buildValueColumns(report, rows, period) {
  const ordered = (report.valueColumns || []).map((item) => ({ ...item }))
  rows.forEach((row) => {
    Object.keys(row.values || {}).forEach((name) => {
      if (!ordered.some((column) => column.name === name)) {
        ordered.push({ name, label: VALUE_LABELS[name] || name })
      }
    })
  })

  return ordered.map((column) => {
    if (column.name === 'current' && period && period.report_date) {
      return { ...column, label: `На ${formatDate(period.report_date)}` }
    }
    if (column.name === 'previous' && period && period.previous_date) {
      return { ...column, label: `На ${formatDate(period.previous_date)}` }
    }
    if (column.name === 'preprevious' && period && period.preprevious_date) {
      return { ...column, label: `На ${formatDate(period.preprevious_date)}` }
    }
    return column
  })
}

function measureLabel(metadata) {
  if (!metadata || metadata.unit === 'rub') {
    return 'руб.'
  }
  return metadata.unit || ''
}

export function normalizeAccountingReport(reportCode, response) {
  const report = getReportByCode(reportCode)
  const rows = normalizeRows(reportCode, response && response.rows)
  return {
    reportCode,
    reportName: (response && response.report_name) || (report ? report.name : reportCode),
    period: (response && response.period) || {},
    organization: (response && response.organization) || {},
    rows,
    valueColumns: buildValueColumns(report || {}, rows, response && response.period),
    measureLabel: measureLabel(response && response.metadata),
    totals: (response && response.totals) || {},
    diagnostics: Array.isArray(response && response.diagnostics) ? response.diagnostics : [],
    diagnosticsSupported: Array.isArray(response && response.diagnostics),
    sourceDocuments: (response && response.source_documents) || [],
    sourceTransfers: (response && response.source_transfers) || [],
    partial: Boolean(response && response.metadata && response.metadata.partial)
  }
}

function normalizeSubcontoRow(row, depth = 0, parentId = '') {
  const id = row.id || `${parentId}-${depth}-${row.label || row.name || 'row'}`
  const children = (row.childs || row.children || []).map((child, index) => {
    return normalizeSubcontoRow(child, depth + 1, `${id}-${index}`)
  })
  return {
    ...row,
    id,
    label: row.label || row.name || row.account_code || 'Строка отчета',
    depth,
    children,
    childs: children
  }
}

export function normalizeSubcontoAnalysisReport(response) {
  const rows = ((response && response.rows) || []).map((row, index) => {
    return normalizeSubcontoRow(row, 0, `subconto-${index}`)
  })
  return {
    reportCode: REPORT_CODES.SUBCONTO,
    reportName: (response && response.report_name) || 'Анализ субконто',
    period: (response && response.period) || {},
    organization: (response && response.organization) || {},
    rows,
    columns: (response && response.columns) || [],
    totals: (response && response.totals) || {},
    diagnostics: Array.isArray(response && response.diagnostics) ? response.diagnostics : [],
    sourceDocuments: (response && response.source_documents) || [],
    sourceTransfers: (response && response.source_transfers) || [],
    metadata: (response && response.metadata) || {}
  }
}

export function normalizeBookRows(rows) {
  return (rows || []).map((row, index) => ({
    ...row,
    line_no: row.line_no || row.row_number || index + 1,
    document_id: row.document_id || row.id,
    invoice_number: row.invoice_number || row.linked_factura_number || row.name,
    invoice_date: row.invoice_date || row.linked_factura_date || row.date,
    counterparty_name: row.counterparty_name || row.contr_name || row.counterparty,
    counterparty_inn: row.counterparty_inn || row.inn,
    counterparty_kpp: row.counterparty_kpp || row.kpp,
    amount: row.amount ?? row.summ_total,
    vat_amount: row.vat_amount ?? row.summ_nds,
    inn_kpp: (row.counterparty_kpp || row.kpp)
      ? `${row.counterparty_inn || row.inn || '—'} / ${row.counterparty_kpp || row.kpp}`
      : (row.counterparty_inn || row.inn),
    operation_code: row.operation_code || '01',
    currency: row.currency || 'RUB'
  }))
}

export function normalizeBookReport(reportCode, response) {
  const report = getReportByCode(reportCode)
  const rows = normalizeBookRows((response && response.rows) || [])
  return {
    reportCode,
    reportName: (response && response.report_name) || (report ? report.name : reportCode),
    rows,
    totals: (response && response.totals) || {},
    diagnostics: Array.isArray(response && response.diagnostics) ? response.diagnostics : [],
    diagnosticsSupported: Array.isArray(response && response.diagnostics),
    sourceDocuments: (response && response.source_documents) || [],
    sourceTransfers: (response && response.source_transfers) || [],
    metadata: (response && response.metadata) || {}
  }
}

export function normalizeVatReport(response) {
  const sections = (response.sections || []).reduce((acc, item) => {
    const key = item.number || String(item.code || '').replace('section_', '')
    if (key) {
      acc[key] = item
    }
    return acc
  }, {})
  const section8Rows = (sections['8'] && sections['8'].rows) || []
  const section9Rows = (sections['9'] && sections['9'].rows) || []
  return {
    summary: {
      totals: response.totals || {},
      sections
    },
    sections,
    incomingRows: normalizeBookRows(section8Rows),
    outgoingRows: normalizeBookRows(section9Rows),
    diagnostics: Array.isArray(response.diagnostics) ? response.diagnostics : [],
    diagnosticsSupported: Array.isArray(response.diagnostics),
    sourceDocuments: response.source_documents || [],
    sourceTransfers: response.source_transfers || [],
    metadata: response.metadata || {}
  }
}

export function isBookReport(reportCode) {
  return reportCode === REPORT_CODES.SELLS_BOOK || reportCode === REPORT_CODES.BUYS_BOOK
}

const LEGACY_COMMON_FIELDS = Object.freeze([
  'audit',
  'currency_measure',
  'date_current',
  'date_previous',
  'date_preprevious',
  'document_date',
  'document_date_unix',
  'document_type',
  'in_org_number',
  'organisation_address',
  'organisation_audit_id',
  'organisation_audit_name',
  'organisation_audit_num',
  'organisation_econ',
  'organisation_form',
  'organisation_inn',
  'organisation_name',
  'report_type'
])

const clone = (value) => JSON.parse(JSON.stringify(value))
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const hasValue = (value) => value !== null && value !== undefined && String(value).trim() !== ''

export class LegacyReportAdapterError extends Error {
  constructor({ code, message, state = 'error', details = [] }) {
    super(message)
    this.name = 'LegacyReportAdapterError'
    this.code = code
    this.state = state
    this.details = details
  }
}

function uniqueReportRows(reportCode) {
  const seen = new Set()
  return (ACCOUNTING_REPORT_ROWS[reportCode] || []).filter((row) => {
    if (seen.has(row.key)) return false
    seen.add(row.key)
    return true
  })
}

function isLegacyRowBody(value) {
  if (!isRecord(value)) return false
  const keys = Object.keys(value)
  return LEGACY_VALUE_ORDER.some((key) => keys.includes(key))
}

function legacyExtensionName(key, value) {
  if (hasValue(value.desc)) return String(value.desc)
  return key
}

export function legacyReportRowDefinitions(reportCode, body) {
  const canonical = uniqueReportRows(reportCode)
  const known = new Set(canonical.map((row) => row.key))
  const extensions = Object.keys(body || {})
    .filter((key) => !known.has(key) && !LEGACY_COMMON_FIELDS.includes(key) && isLegacyRowBody(body[key]))
    .sort()
    .map((key) => ({
      key,
      name: legacyExtensionName(key, body[key]),
      code: '',
      section: 'Дополнительные показатели сохраненной формы',
      isTotal: false
    }))
  return [...canonical, ...extensions]
}

function legacyRowValues(row) {
  const keys = Object.keys(row).filter((key) => key !== 'desc' && !isRecord(row[key]) && !Array.isArray(row[key]))
  const ordered = [
    ...LEGACY_VALUE_ORDER.filter((key) => keys.includes(key)),
    ...keys.filter((key) => !LEGACY_VALUE_ORDER.includes(key)).sort()
  ]
  return Object.fromEntries(ordered.map((key) => [key, row[key]]))
}

function legacyMeasureLabel(value) {
  if (isRecord(value)) return value.label || value.value || ''
  return value || ''
}

function ensureLegacyHistory(data, historyId) {
  if (!historyId || historyId === 'last') return
  const exists = Array.isArray(data.changes) && data.changes.some((change) => {
    return String(change.id) === String(historyId)
  })
  if (!exists) {
    throw new LegacyReportAdapterError({
      code: 'history_not_found',
      state: 'not-found',
      message: `Версия истории №${historyId} не найдена`
    })
  }
}

function validateLegacyBody(reportCode, data) {
  const definition = getLegacyReportDefinition(reportCode)
  if (!definition) {
    throw new LegacyReportAdapterError({
      code: 'report_type_not_found',
      state: 'not-found',
      message: 'Тип сохраненного отчета не поддерживается'
    })
  }
  if (!isRecord(data) || !isRecord(data.body)) {
    throw new LegacyReportAdapterError({
      code: 'malformed_report',
      message: 'Сервер вернул некорректные данные сохраненного отчета',
      details: ['body']
    })
  }

  const body = data.body
  const actualType = String(body.report_type || '')
  if (actualType && !definition.acceptedTypes.includes(actualType)) {
    throw new LegacyReportAdapterError({
      code: 'report_type_mismatch',
      state: 'not-found',
      message: 'Сохраненный отчет не соответствует выбранному типу'
    })
  }

  const missing = []
  if (!actualType) missing.push('report_type')
  if (body.document_type !== 'report') missing.push('document_type')
  if (!hasValue(body.in_org_number)) missing.push('in_org_number')
  if (!hasValue(body.document_date) || !moment(body.document_date, moment.ISO_8601, true).isValid()) {
    missing.push('document_date')
  }
  if (!hasValue(body.date_current)) missing.push('date_current')
  if (!hasValue(body.date_previous)) missing.push('date_previous')
  if (!hasValue(body.organisation_name)) missing.push('organisation_name')
  if (!hasValue(body.organisation_inn)) missing.push('organisation_inn')
  if (!hasValue(body.currency_measure)) missing.push('currency_measure')

  uniqueReportRows(reportCode).forEach((row) => {
    const value = body[row.key]
    if (!isRecord(value)) {
      missing.push(row.key)
      return
    }
    if (Object.keys(legacyRowValues(value)).length === 0) {
      missing.push(`${row.key}.*`)
    }
  })

  if (missing.length) {
    throw new LegacyReportAdapterError({
      code: 'malformed_report',
      message: 'Сохраненный отчет поврежден или содержит неполные данные',
      details: missing
    })
  }
  return { body, definition }
}

export function legacyReportValueColumns(reportCode, body) {
  const keys = new Set()
  legacyReportRowDefinitions(reportCode, body).forEach((row) => {
    Object.keys(legacyRowValues(body[row.key] || {})).forEach((key) => keys.add(key))
  })
  return [
    ...LEGACY_VALUE_ORDER.filter((key) => keys.has(key)),
    ...[...keys].filter((key) => !LEGACY_VALUE_ORDER.includes(key)).sort()
  ].map((name) => ({ name, label: LEGACY_VALUE_LABELS[name] || name }))
}

export function normalizeLegacyReportDocument(reportCode, data, { historyId = 'last' } = {}) {
  ensureLegacyHistory(data, historyId)
  const { body, definition } = validateLegacyBody(reportCode, data)
  const rowDefinitions = legacyReportRowDefinitions(reportCode, body)
  const rows = rowDefinitions.map((row) => ({
    ...row,
    values: legacyRowValues(body[row.key])
  }))
  const normalized = normalizeAccountingReport(reportCode, {
    report_name: (getReportByCode(reportCode) || {}).name,
    period: {
      report_date: body.date_current,
      previous_date: body.date_previous,
      preprevious_date: body.date_preprevious
    },
    organization: {
      name: body.organisation_name,
      inn: body.organisation_inn,
      address: body.organisation_address || ''
    },
    rows,
    metadata: { unit: legacyMeasureLabel(body.currency_measure) },
    diagnostics: [],
    source_documents: [],
    source_transfers: []
  })
  const known = new Set([
    ...LEGACY_COMMON_FIELDS,
    ...rowDefinitions.map((row) => row.key)
  ])
  return {
    ...normalized,
    body: clone(body),
    definition,
    document: {
      id: data.id,
      name: data.name || normalized.reportName,
      isActive: data.is_active !== false,
      changes: Array.isArray(data.changes) ? data.changes : [],
      date: data.date,
      dateDisplay: data.date_display || '',
      documentType: data.document_type || body.document_type
    },
    rowDefinitions,
    valueColumns: legacyReportValueColumns(reportCode, body),
    unknownFields: Object.keys(body).filter((key) => !known.has(key))
  }
}

export function legacyDateInput(value) {
  if (!hasValue(value)) return ''
  if (typeof value === 'number') {
    const date = value > 100000000000 ? moment(value) : moment.unix(value)
    return date.isValid() ? date.format('DD.MM.YYYY') : ''
  }
  const strictDisplay = moment(String(value), 'DD.MM.YYYY', true)
  if (strictDisplay.isValid()) return strictDisplay.format('DD.MM.YYYY')
  const iso = moment(String(value), moment.ISO_8601, true)
  return iso.isValid() ? iso.format('DD.MM.YYYY') : ''
}

function dateValueLike(original, input) {
  const parsed = moment(input, 'DD.MM.YYYY', true)
  if (!parsed.isValid()) return original
  if (typeof original === 'number') {
    return original > 100000000000 ? parsed.valueOf() : parsed.unix()
  }
  if (moment(String(original), moment.ISO_8601, true).isValid()) {
    return parsed.toISOString(true)
  }
  return input
}

export function buildLegacyReportSaveBody(body, dateInputs, savedDateInputs) {
  const next = clone(body)
  const dateKeys = ['date_current', 'date_previous', 'date_preprevious']
  dateKeys.forEach((key) => {
    if (dateInputs[key] !== savedDateInputs[key]) {
      next[key] = dateValueLike(next[key], dateInputs[key])
    }
  })
  if (dateInputs.document_date !== savedDateInputs.document_date) {
    const parsed = moment(dateInputs.document_date, 'DD.MM.YYYY', true)
    if (parsed.isValid()) {
      next.document_date = parsed.toISOString(true)
      if (Object.prototype.hasOwnProperty.call(next, 'document_date_unix')) {
        next.document_date_unix = parsed.unix()
      }
    }
  }
  return next
}
